import React, { useMemo, useState } from 'react';
import {
  Alert, App as AntApp, Button, Card, Checkbox, Collapse, Descriptions, Divider, Empty, Flex,
  Form, Input, Modal, Progress, Select, Space, Tag, Typography,
} from 'antd';
import {
  CheckCircleOutlined, EditOutlined, HistoryOutlined, PlusOutlined,
  SafetyCertificateOutlined, StopOutlined,
} from '@ant-design/icons';
import { conflitoDeRevisao, mensagemErro, postarNexo } from './api';
import {
  Capacidade, CHECKLIST_CHAVES, Checklist, ChecklistChave, Colaborador, GithubStatus, InicioNexo,
  PerfilUso, StatusVersao, VersaoCapacidade, checklistChavesDoPerfil, checklistVazio,
  githubLoginValido, githubPerfilUrl,
} from './types';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;

const checklistRotulos: Record<string, string> = {
  conta_github: 'Conta pessoal do GitHub criada',
  dois_fatores: 'Proteção em duas etapas ativada',
  convite_github: 'Acesso aos repositórios recebido',
  ia_instalada: 'Ferramenta de IA preparada',
  kit_preparado: 'Pasta preparada com a IA',
  primeira_entrega: 'Primeira melhoria entregue',
};
const perfilRotulos: Record<PerfilUso, string> = {
  desenvolve: 'Desenvolve com IA',
  homologa: 'Confere resultados',
  usa: 'Utiliza ferramentas prontas',
};
const perfilDescricoes: Record<PerfilUso, string> = {
  desenvolve: 'Trabalha no Codex ou Claude Code instalado no computador para criar melhorias.',
  homologa: 'Compara os resultados com o critério combinado. O responsável autorizado registra o aceite no NEXO.',
  usa: 'Executa a ferramenta no produto ou canal indicado na ficha, como a Toolbox. O NEXO é o catálogo e não executa a ferramenta.',
};
const ferramentaRotulos = { a_definir: 'A definir', codex: 'Codex', claude: 'Claude Code', ambos: 'Codex e Claude Code' };
const githubRotulos: Record<GithubStatus, string> = {
  nao_necessario: 'Não necessário', aguardando_login: 'Aguardando usuário', login_informado: 'Usuário informado',
  convite_enviado: 'Convite enviado', aceito: 'Acesso aceito',
};
const dominioRotulos = { engenharia: 'Engenharia', gestao: 'Gestão', transversal: 'Transversal' };
const tipoRotulos = { rotina: 'Rotina', biblioteca: 'Biblioteca', api_mcp: 'API / MCP', produto: 'Produto' };
const statusRotulos: Record<StatusVersao, string> = {
  rascunho: 'Rascunho', em_homologacao: 'Em homologação', homologada: 'Homologada', suspensa: 'Suspensa',
};
const statusCores: Record<StatusVersao, string> = {
  rascunho: 'default', em_homologacao: 'processing', homologada: 'success', suspensa: 'error',
};

function rotuloChecklist(chave: ChecklistChave, perfil: PerfilUso) {
  if (perfil === 'homologa' && chave === 'kit_preparado') return 'Demonstração e critérios de conferência recebidos';
  if (perfil === 'homologa' && chave === 'primeira_entrega') return 'Primeiro resultado conferido com o responsável';
  if (perfil === 'usa' && chave === 'kit_preparado') return 'Orientação ou acesso para usar a ferramenta recebido';
  if (perfil === 'usa' && chave === 'primeira_entrega') return 'Primeiro uso concluído';
  return checklistRotulos[chave];
}

function progresso(checklist: Partial<Checklist> | undefined, perfil: PerfilUso) {
  const chaves = checklistChavesDoPerfil(perfil);
  return Math.round(chaves.filter(chave => checklist?.[chave]).length / chaves.length * 100);
}

interface PainelProps { dados: InicioNexo; recarregar: () => Promise<void> }

export function PainelEquipe({ dados, recarregar }: PainelProps) {
  const { message, modal } = AntApp.useApp();
  const [form] = Form.useForm();
  const [aberto, setAberto] = useState(false);
  const [editando, setEditando] = useState<Colaborador | null>(null);
  const [salvando, setSalvando] = useState(false);
  const perfilSelecionado = Form.useWatch('perfil_uso', form) as PerfilUso | undefined;
  const githubLoginForm = Form.useWatch('github_login', form) as string | undefined;
  const githubUrlForm = githubPerfilUrl(githubLoginForm);
  const opcoesUsuariosErp = useMemo(() => {
    const idsEmUso = new Set(dados.colaboradores
      .filter(colaborador => colaborador.id !== editando?.id)
      .map(colaborador => colaborador.usuario_erp_id));
    const opcoes = (dados.usuarios_erp || []).map(usuario => ({
      value: usuario.id,
      label: `${usuario.nome || 'Sem nome cadastrado'} — ${usuario.email}${usuario.ativo ? '' : ' (conta desativada)'}`,
      disabled: !usuario.ativo || !usuario.nome || idsEmUso.has(usuario.id),
    }));
    if (editando && !opcoes.some(opcao => opcao.value === editando.usuario_erp_id)) {
      opcoes.unshift({
        value: editando.usuario_erp_id,
        label: `${editando.nome} — ${editando.email} (não existe mais no ERP)`,
        disabled: true,
      });
    }
    return opcoes;
  }, [dados.colaboradores, dados.usuarios_erp, editando]);

  const abrir = (colaborador?: Colaborador) => {
    setEditando(colaborador || null);
    form.resetFields();
    form.setFieldsValue(colaborador ? {
      ...colaborador,
      checklist_itens: CHECKLIST_CHAVES.filter(chave => colaborador.checklist?.[chave]),
    } : {
      perfil_uso: 'usa', ferramenta: 'a_definir', repositorios: [], github_status: 'nao_necessario',
      checklist_itens: [],
    });
    setAberto(true);
  };

  const salvarAdmin = async () => {
    const valores = await form.validateFields();
    const marcados = valores.checklist_itens || [];
    const checklist = CHECKLIST_CHAVES.reduce((resultado, chave) => ({ ...resultado, [chave]: marcados.includes(chave) }), checklistVazio());
    setSalvando(true);
    try {
      await postarNexo({
        action: 'colaborador_salvar',
        colaborador: {
          ...(editando ? { id: editando.id, revisao: editando.revisao } : {}),
          usuario_erp_id: valores.usuario_erp_id, github_login: valores.github_login?.trim() || null,
          perfil_uso: valores.perfil_uso, ferramenta: valores.ferramenta, repositorios: valores.repositorios || [],
          github_status: valores.github_status, plano_ia: valores.plano_ia?.trim() || '',
          observacoes: valores.observacoes?.trim() || '', checklist,
        },
      });
      message.success(editando ? 'Ficha atualizada.' : 'Ficha de preparação criada.');
      setAberto(false);
      await recarregar();
    } catch (erro) {
      if (conflitoDeRevisao(erro)) {
        modal.warning({ title: 'Esta ficha mudou', content: 'Outra pessoa atualizou esta ficha. Recarregamos os dados; confira antes de salvar novamente.' });
        setAberto(false);
        await recarregar();
      } else message.error(mensagemErro(erro));
    } finally { setSalvando(false); }
  };

  if (!dados.permissoes.gerenciar_colaboradores) return dados.participante ? (
    <MinhaEntrada participante={dados.participante} recarregar={recarregar} />
  ) : <div className="nexo-my-entry">
    <Title level={3}>Minha entrada no NEXO</Title>
    <Alert type="info" showIcon
      message="Seu acesso de consulta já está liberado"
      description="Você pode consultar o acervo e baixar os materiais mesmo sem uma ficha de preparação. A ficha é opcional; se precisar de acompanhamento, peça a um responsável autorizado para cadastrá-la." />
    <Space wrap style={{ marginTop: 16 }}>
      <Button type="primary" href="/nexo?aba=acervo">Abrir acervo</Button>
      <Button href="/nexo?aba=materiais">Ver materiais e downloads</Button>
    </Space>
  </div>;

  return (
    <div>
      <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12} className="nexo-section-heading">
        <div>
          <Title level={3}>Colaboradores</Title>
          <Paragraph type="secondary">Cadastre a preparação e acompanhe cada pessoa. Autorizações ficam centralizadas em Administração → Permissões.</Paragraph>
        </div>
        <Space wrap>
          <Button href="/admin/permissoes">Usuários e permissões do ERP</Button>
          <Button href="https://github.com/orgs/hydrodataeng/people" target="_blank" rel="noreferrer">Membros no GitHub</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => abrir()}>Nova ficha de entrada</Button>
        </Space>
      </Flex>
      <Alert type="info" showIcon message="Esta tela não libera acessos, envia convites nem cria contas. Use Administração → Permissões para definir o acesso ao NEXO." />
      <Paragraph type="secondary" className="nexo-role-note">A participação descreve o papel da pessoa. Ela não altera autorizações nem grupos.</Paragraph>

      <div className="nexo-card-grid nexo-equipe-grid">
        {dados.colaboradores.map(colaborador => (
          <Card key={colaborador.id} className="nexo-person-card" title={
            <Flex justify="space-between" gap={8} align="center">
              <span>{colaborador.nome}</span>
              <Tag color={colaborador.acesso_liberado ? 'success' : 'default'}>
                {colaborador.acesso_liberado ? 'Acesso central liberado' : 'Sem acesso central'}
              </Tag>
              {!colaborador.usuario_erp_ativo && <Tag color="error">Conta ERP indisponível</Tag>}
            </Flex>
          } actions={[
            <Button type="text" icon={<EditOutlined />} onClick={() => abrir(colaborador)}>Editar ficha</Button>,
          ]}>
            <Text type="secondary">{colaborador.email}</Text>
            <Flex gap={6} wrap="wrap" style={{ marginTop: 10 }}>
              <Tag>{perfilRotulos[colaborador.perfil_uso]}</Tag>
              {(colaborador.perfil_uso !== 'usa' || colaborador.ferramenta !== 'a_definir') && <Tag>{ferramentaRotulos[colaborador.ferramenta]}</Tag>}
              {(colaborador.perfil_uso !== 'usa' || colaborador.github_status !== 'nao_necessario') && <Tag>{githubRotulos[colaborador.github_status]}</Tag>}
            </Flex>
            <Paragraph type="secondary" className="nexo-role-description">{perfilDescricoes[colaborador.perfil_uso]}</Paragraph>
            {githubPerfilUrl(colaborador.github_login) && <a href={githubPerfilUrl(colaborador.github_login)!} target="_blank" rel="noreferrer">Abrir perfil de {colaborador.github_login} no GitHub</a>}
            <Text className="nexo-progress-label">Etapas informadas pela pessoa</Text>
            <Progress percent={progresso(colaborador.checklist, colaborador.perfil_uso)} size="small" />
          </Card>
        ))}
      </div>
      {!dados.colaboradores.length && <Empty description="Nenhuma ficha cadastrada. Comece pela pessoa que fará o piloto." />}

      <Modal open={aberto} onCancel={() => setAberto(false)} onOk={salvarAdmin} confirmLoading={salvando}
        title={editando ? 'Editar ficha de entrada' : 'Nova ficha de entrada'} okText="Salvar ficha" cancelText="Cancelar"
        width="94vw" style={{ maxWidth: 980, top: 16 }} styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }} destroyOnClose>
        <Form form={form} layout="vertical" className="nexo-form-grid">
          <Form.Item label="Pessoa cadastrada no ERP" name="usuario_erp_id"
            extra={editando ? 'A pessoa vinculada à ficha não pode ser trocada.' : 'Nome e e-mail vêm do cadastro de usuários do ERP.'}
            rules={[{ required: true, message: 'Selecione uma pessoa já cadastrada no ERP.' }]}>
            <Select showSearch optionFilterProp="label" disabled={!!editando}
              placeholder="Selecione pelo nome ou e-mail" options={opcoesUsuariosErp} />
          </Form.Item>
          <Form.Item label="Como participará" name="perfil_uso" rules={[{ required: true }]}>
            <Select options={Object.entries(perfilRotulos).map(([value, label]) => ({ value, label }))} />
          </Form.Item>
          <Alert className="nexo-form-wide nexo-profile-explanation" type="info" showIcon
            message={perfilSelecionado ? perfilDescricoes[perfilSelecionado] : 'Escolha como a pessoa participará.'}
            description="Esse perfil registra a participação. Ele não libera acessos, muda autorizações ou altera grupos." />
          <Collapse className="nexo-form-wide nexo-followup" items={[{
            key: 'acompanhamento', label: 'Acompanhamento opcional', forceRender: true,
            children: <div className="nexo-followup-grid">
              <Form.Item label="Ferramenta de IA" name="ferramenta" rules={[{ required: true }]}>
                <Select options={Object.entries(ferramentaRotulos).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
              <Form.Item label="Usuário pessoal do GitHub" name="github_login"
                extra="É apenas um registro. O NEXO não conecta a conta, verifica o usuário nem envia convite."
                rules={[{ validator: (_, valor) => githubLoginValido(valor) ? Promise.resolve() : Promise.reject(new Error('Use somente letras, números e hífens simples, sem hífen no início ou no fim.')) }]}>
                <Input placeholder="Ex.: lucasmartins" suffix={githubUrlForm ? <a href={githubUrlForm} target="_blank" rel="noreferrer">Abrir perfil</a> : undefined} />
              </Form.Item>
              <Form.Item label="Situação no GitHub" name="github_status" rules={[{ required: true }]}>
                <Select options={Object.entries(githubRotulos).map(([value, label]) => ({ value, label }))} />
              </Form.Item>
              <Form.Item label="Repositórios necessários" name="repositorios">
                <Checkbox.Group options={[{ label: 'NEXO', value: 'nexo' }, { label: 'ERP', value: 'dre-system' }, { label: 'Toolbox', value: 'toolbox' }]} />
              </Form.Item>
              <Form.Item label="Assinatura de IA combinada (opcional)" name="plano_ia" className="nexo-form-wide"
                extra="Serve somente para acompanhar o combinado. Ex.: plano individual já existente. Este registro não contrata nem desbloqueia IA.">
                <TextArea rows={2} placeholder="Ex.: plano individual já existente" />
              </Form.Item>
              <Form.Item label="Observações" name="observacoes" className="nexo-form-wide"><TextArea rows={2} /></Form.Item>
              <Form.Item label="Etapas informadas pela pessoa (opcional)" name="checklist_itens" className="nexo-form-wide"
                extra="As caixas e o progresso são acompanhamento manual. O NEXO não inspeciona o computador nem libera acesso.">
                <Checkbox.Group className="nexo-checklist" options={CHECKLIST_CHAVES.map(value => ({
                  value,
                  label: rotuloChecklist(value, perfilSelecionado || 'usa'),
                  style: checklistChavesDoPerfil(perfilSelecionado || 'usa').includes(value) ? undefined : { display: 'none' },
                }))} />
              </Form.Item>
            </div>,
          }]} />
        </Form>
        {!editando && <Alert type="info" showIcon message="A ficha registra apenas preparação e acompanhamento. Defina o acesso em Administração → Permissões." />}
      </Modal>
    </div>
  );
}

function MinhaEntrada({ participante, recarregar }: { participante: Colaborador; recarregar: () => Promise<void> }) {
  const { message } = AntApp.useApp();
  const [github, setGithub] = useState(participante.github_login || '');
  const [checklist, setChecklist] = useState<Checklist>({ ...checklistVazio(), ...participante.checklist });
  const [salvando, setSalvando] = useState(false);
  const githubValido = githubLoginValido(github);
  const githubUrl = githubPerfilUrl(github);
  const chavesVisiveis = checklistChavesDoPerfil(participante.perfil_uso);
  const mostrarGithub = participante.perfil_uso === 'desenvolve' || !!github.trim();

  const salvar = async () => {
    setSalvando(true);
    try {
      await postarNexo({ action: 'minha_entrada_salvar', revisao: participante.revisao, github_login: github.trim(), checklist });
      message.success('Sua preparação foi atualizada.');
      await recarregar();
    } catch (erro) {
      message.error(conflitoDeRevisao(erro) ? 'Sua ficha mudou. Recarregue a página e confira os dados.' : mensagemErro(erro));
    } finally { setSalvando(false); }
  };

  return <div>
    <Title level={3}>Minha entrada no NEXO</Title>
    <Paragraph type="secondary">Confira como você participará e, se quiser, atualize o acompanhamento.</Paragraph>
    <Card className="nexo-my-entry">
      <Descriptions size="small" column={{ xs: 1, sm: 2 }} items={[
        { key: 'nome', label: 'Nome', children: participante.nome },
        { key: 'email', label: 'E-mail', children: participante.email },
        { key: 'perfil', label: 'Participação', children: perfilRotulos[participante.perfil_uso] },
      ]} />
      <Alert className="nexo-my-role" type="info" showIcon message={perfilDescricoes[participante.perfil_uso]}
        description="Sua participação não muda autorizações nem grupos." />
      <Collapse className="nexo-followup" items={[{
        key: 'acompanhamento', label: 'Acompanhamento opcional', forceRender: true,
        children: <div>
          {(participante.perfil_uso === 'desenvolve' || participante.ferramenta !== 'a_definir') &&
            <Descriptions className="nexo-followup-summary" size="small" column={1} items={[
              { key: 'ferramenta', label: 'Ferramenta combinada', children: ferramentaRotulos[participante.ferramenta] },
            ]} />}
          {mostrarGithub && <div className="nexo-github-field">
            <label className="nexo-field-label">Seu usuário pessoal do GitHub</label>
            <Input value={github} status={githubValido ? undefined : 'error'} onChange={e => setGithub(e.target.value)}
              placeholder="Digite somente o nome de usuário" suffix={githubUrl ? <a href={githubUrl} target="_blank" rel="noreferrer">Abrir perfil</a> : undefined} />
            <Text type={githubValido ? 'secondary' : 'danger'}>
              {githubValido ? 'É apenas um registro: o NEXO não conecta, verifica ou envia convite.' : 'Use somente letras, números e hífens simples, sem hífen no início ou no fim.'}
            </Text>
          </div>}
          <Text type="secondary">As caixas e o progresso são acompanhamento manual. Eles não inspecionam o computador nem liberam acesso.</Text>
          <div className="nexo-self-checklist">
            {chavesVisiveis.map(chave => <Checkbox key={chave} checked={checklist[chave]}
              onChange={e => setChecklist(atual => ({ ...atual, [chave]: e.target.checked }))}>{rotuloChecklist(chave, participante.perfil_uso)}</Checkbox>)}
          </div>
          <Progress percent={progresso(checklist, participante.perfil_uso)} />
          <Button type="primary" loading={salvando} disabled={!githubValido} onClick={salvar}>Salvar acompanhamento</Button>
        </div>,
      }]} />
    </Card>
  </div>;
}

export function PainelAcervo({ dados, recarregar }: PainelProps) {
  const { message, modal } = AntApp.useApp();
  const [busca, setBusca] = useState('');
  const [formCapacidade] = Form.useForm();
  const [formVersao] = Form.useForm();
  const [capacidadeAberta, setCapacidadeAberta] = useState<Capacidade | null>(null);
  const [versaoAberta, setVersaoAberta] = useState<{ capacidade: Capacidade; versao?: VersaoCapacidade } | null>(null);
  const [salvando, setSalvando] = useState(false);
  const podeEditar = dados.permissoes.editar_acervo;
  const podeHomologar = dados.permissoes.homologar;
  const podeVerHistorico = podeEditar || podeHomologar;

  const capacidades = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const visiveis = dados.capacidades.map(capacidade => ({
      ...capacidade,
      versoes: podeVerHistorico ? capacidade.versoes : capacidade.versoes.filter(versao => versao.status === 'homologada'),
    }));
    if (!termo) return visiveis;
    return visiveis.filter(item => [item.nome, item.codigo, item.descricao, item.responsavel, dominioRotulos[item.dominio]]
      .some(valor => valor?.toLowerCase().includes(termo)));
  }, [busca, dados.capacidades, podeVerHistorico]);

  const abrirCapacidade = (capacidade?: Capacidade) => {
    if (!podeEditar) return;
    setCapacidadeAberta(capacidade || ({ id: '', codigo: '', nome: '', dominio: 'engenharia', tipo: 'rotina', descricao: '', responsavel: '', repositorio: '', revisao: 0, versoes: [] }));
    formCapacidade.resetFields();
    formCapacidade.setFieldsValue(capacidade || { dominio: 'engenharia', tipo: 'rotina' });
  };

  const salvarCapacidade = async () => {
    if (!podeEditar) return;
    const valores = await formCapacidade.validateFields();
    setSalvando(true);
    try {
      await postarNexo({ action: 'capacidade_salvar', capacidade: {
        ...(capacidadeAberta?.id ? { id: capacidadeAberta.id, revisao: capacidadeAberta.revisao } : {}), ...valores,
      } });
      message.success('Capacidade salva.'); setCapacidadeAberta(null); await recarregar();
    } catch (erro) { await tratarErroMutacao(erro, modal, message, recarregar, () => setCapacidadeAberta(null)); }
    finally { setSalvando(false); }
  };

  const abrirVersao = (capacidade: Capacidade, versao?: VersaoCapacidade) => {
    if (!podeEditar) return;
    setVersaoAberta({ capacidade, versao });
    formVersao.resetFields();
    formVersao.setFieldsValue(versao || {});
  };

  const salvarVersao = async () => {
    if (!versaoAberta || !podeEditar) return;
    const valores = await formVersao.validateFields();
    setSalvando(true);
    try {
      await postarNexo({ action: 'versao_salvar', versao: {
        ...(versaoAberta.versao ? { id: versaoAberta.versao.id, revisao: versaoAberta.versao.revisao } : {}),
        capacidade_id: versaoAberta.capacidade.id, ...valores,
      } });
      message.success('Versão salva como rascunho.'); setVersaoAberta(null); await recarregar();
    } catch (erro) { await tratarErroMutacao(erro, modal, message, recarregar, () => setVersaoAberta(null)); }
    finally { setSalvando(false); }
  };

  const mudarEstado = (versao: VersaoCapacidade, status: StatusVersao) => {
    const envioParaHomologacao = versao.status === 'rascunho' && status === 'em_homologacao';
    if ((envioParaHomologacao && !podeEditar) || (!envioParaHomologacao && !podeHomologar)) return;
    let parecer = '';
    const devolvendo = status === 'rascunho';
    modal.confirm({
      title: status === 'homologada' ? (versao.status === 'suspensa' ? 'Reativar esta versão?' : 'Aceitar e homologar esta versão?') : status === 'suspensa' ? 'Suspender esta versão?' : devolvendo ? 'Devolver esta versão para rascunho?' : 'Encaminhar para homologação?',
      icon: status === 'suspensa' ? <StopOutlined /> : <SafetyCertificateOutlined />,
      content: <div><Paragraph>{status === 'homologada' ? 'Confirme que entradas, saídas, limites, execução, evidências e o código publicado foram conferidos.' : status === 'suspensa' ? 'A versão deixará de aparecer como disponível para uso.' : devolvendo ? 'A versão voltará para edição antes de uma nova conferência.' : 'A versão ficará pronta para a revisão do responsável autorizado.'}</Paragraph>
        <TextArea rows={4} placeholder={status === 'homologada' ? 'Parecer humano obrigatório' : 'Registre o motivo ou orientação'} onChange={e => { parecer = e.target.value; }} /></div>,
      okText: status === 'homologada' ? (versao.status === 'suspensa' ? 'Reativar' : 'Aceitar') : status === 'suspensa' ? 'Suspender' : devolvendo ? 'Devolver' : 'Encaminhar',
      okButtonProps: { danger: status === 'suspensa' }, cancelText: 'Cancelar',
      onOk: async () => {
        if (['homologada', 'suspensa'].includes(status) && !parecer.trim()) {
          message.warning(status === 'suspensa' ? 'Registre o motivo antes de suspender.' : 'Escreva o parecer humano antes de homologar.');
          return Promise.reject();
        }
        try {
          await postarNexo({ action: 'versao_estado', id: versao.id, revisao: versao.revisao, status, parecer: parecer.trim() });
          message.success(status === 'homologada' ? (versao.status === 'suspensa' ? 'Versão reativada.' : 'Versão homologada.') : status === 'suspensa' ? 'Versão suspensa.' : devolvendo ? 'Versão devolvida para rascunho.' : 'Versão encaminhada.');
          await recarregar();
        } catch (erro) {
          if (conflitoDeRevisao(erro)) await recarregar();
          message.error(conflitoDeRevisao(erro) ? 'A versão mudou. Recarregamos os dados; confira antes de tentar novamente.' : mensagemErro(erro));
          throw erro;
        }
      },
    });
  };

  return <div>
    <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12} className="nexo-section-heading">
      <div><Title level={3}>Acervo de capacidades</Title><Paragraph type="secondary">Encontre rotinas e ferramentas já validadas antes de começar uma melhoria.</Paragraph></div>
      {podeEditar && <Button type="primary" icon={<PlusOutlined />} onClick={() => abrirCapacidade()}>Cadastrar capacidade</Button>}
    </Flex>
    <Input.Search allowClear value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por nome, código, área ou responsável" className="nexo-search" />
    {!capacidades.length ? <Empty description={dados.capacidades.length ? 'Nenhuma capacidade corresponde à busca.' : 'O acervo ainda não tem capacidades cadastradas.'} /> :
      <div className="nexo-card-grid nexo-acervo-grid">{capacidades.map(capacidade => <Card key={capacidade.id} className="nexo-cap-card"
        title={<div><Text code>{capacidade.codigo}</Text><Title level={4}>{capacidade.nome}</Title></div>}
        extra={podeEditar && <Button type="text" icon={<EditOutlined />} onClick={() => abrirCapacidade(capacidade)}>Editar</Button>}>
        <Flex gap={6} wrap="wrap"><Tag color="blue">{dominioRotulos[capacidade.dominio]}</Tag><Tag>{tipoRotulos[capacidade.tipo]}</Tag></Flex>
        <Paragraph ellipsis={{ rows: 3, expandable: true, symbol: 'ler mais' }}>{capacidade.descricao}</Paragraph>
        <Text type="secondary">Responsável: {capacidade.responsavel}</Text>
        <Divider />
        <Flex justify="space-between" align="center"><Text strong>{podeVerHistorico ? 'Histórico de versões' : 'Versões homologadas'}</Text>{podeEditar && <Button size="small" icon={<PlusOutlined />} onClick={() => abrirVersao(capacidade)}>Nova versão</Button>}</Flex>
        {!capacidade.versoes?.length ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={podeVerHistorico ? 'Nenhuma versão cadastrada' : 'Nenhuma versão homologada disponível'} /> :
          <Collapse ghost className="nexo-versions" items={capacidade.versoes.map(versao => ({ key: versao.id,
            label: <Flex gap={8} wrap="wrap" align="center"><Text strong>{versao.versao}</Text><Tag color={statusCores[versao.status]}>{statusRotulos[versao.status]}</Tag></Flex>,
            children: <VersaoDetalhe versao={versao} podeEditar={podeEditar} podeHomologar={podeHomologar} editar={() => abrirVersao(capacidade, versao)} mudarEstado={mudarEstado} />,
          }))} />}
      </Card>)}</div>}

    <Modal open={!!capacidadeAberta} onCancel={() => setCapacidadeAberta(null)} onOk={salvarCapacidade} confirmLoading={salvando}
      title={capacidadeAberta?.id ? 'Editar capacidade' : 'Cadastrar capacidade'} okText="Salvar" cancelText="Cancelar"
      width="94vw" style={{ maxWidth: 900, top: 16 }} styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }} destroyOnClose>
      <Form form={formCapacidade} layout="vertical" className="nexo-form-grid">
        <Form.Item label="Código estável" name="codigo" rules={[{ required: true }, { pattern: /^[a-z0-9]+(?:-[a-z0-9]+)*$/, message: 'Use letras minúsculas, números e hífens.' }]}><Input placeholder="ex.: conferir-medicoes" /></Form.Item>
        <Form.Item label="Nome" name="nome" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item label="Domínio" name="dominio" rules={[{ required: true }]}><Select options={Object.entries(dominioRotulos).map(([value, label]) => ({ value, label }))} /></Form.Item>
        <Form.Item label="Tipo" name="tipo" rules={[{ required: true }]}><Select options={Object.entries(tipoRotulos).map(([value, label]) => ({ value, label }))} /></Form.Item>
        <Form.Item label="Descrição" name="descricao" className="nexo-form-wide" rules={[{ required: true }]}><TextArea rows={3} /></Form.Item>
        <Form.Item label="Responsável" name="responsavel" rules={[{ required: true }]}><Input /></Form.Item>
        <Form.Item label="Repositório" name="repositorio" rules={[{ required: true }, { type: 'url', message: 'Informe a URL completa.' }]}><Input placeholder="https://github.com/hydrodataeng/..." /></Form.Item>
      </Form>
    </Modal>

    <Modal open={!!versaoAberta} onCancel={() => setVersaoAberta(null)} onOk={salvarVersao} confirmLoading={salvando}
      title={`${versaoAberta?.versao ? 'Editar' : 'Nova'} versão${versaoAberta ? ` — ${versaoAberta.capacidade.nome}` : ''}`} okText="Salvar versão" cancelText="Cancelar"
      width="94vw" style={{ maxWidth: 1100, top: 16 }} styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }} destroyOnClose>
      <Form form={formVersao} layout="vertical" className="nexo-form-grid">
        <Form.Item label="Versão" name="versao" rules={[{ required: true }]}><Input placeholder="ex.: 1.0.0" /></Form.Item>
        <Form.Item label="Identificador da versão do código" name="commit_sha" extra="Peça à IA o SHA completo da versão que será avaliada e cole aqui." rules={[{ required: true }, { pattern: /^[0-9a-fA-F]{40}$/, message: 'Cole os 40 caracteres informados pela IA.' }]}><Input className="nexo-mono" /></Form.Item>
        {(['entradas', 'saidas', 'limites', 'execucao', 'evidencias'] as const).map(campo => <Form.Item key={campo} label={{ entradas: 'O que recebe', saidas: 'O que entrega', limites: 'Limites e exceções', execucao: 'Como usar', evidencias: 'Evidências e orientação de teste' }[campo]} name={campo} className="nexo-form-wide" rules={[{ required: true }]}><TextArea rows={3} /></Form.Item>)}
      </Form>
    </Modal>
  </div>;
}

function VersaoDetalhe({ versao, podeEditar, podeHomologar, editar, mudarEstado }: {
  versao: VersaoCapacidade;
  podeEditar: boolean;
  podeHomologar: boolean;
  editar: () => void;
  mudarEstado: (v: VersaoCapacidade, s: StatusVersao) => void;
}) {
  return <div>
    {versao.capacidade_snapshot && <Card size="small" className="nexo-version-snapshot" title="Contexto aprovado nesta versão">
      <Descriptions size="small" column={{ xs: 1, sm: 2 }} items={[
        { key: 'nome', label: 'Capacidade', children: `${versao.capacidade_snapshot.nome} (${versao.capacidade_snapshot.codigo})` },
        { key: 'dominio', label: 'Área e tipo', children: `${dominioRotulos[versao.capacidade_snapshot.dominio]} · ${tipoRotulos[versao.capacidade_snapshot.tipo]}` },
        { key: 'responsavel', label: 'Responsável', children: versao.capacidade_snapshot.responsavel },
        { key: 'repositorio', label: 'Repositório', children: <a href={versao.capacidade_snapshot.repositorio} target="_blank" rel="noreferrer">Abrir repositório</a> },
        { key: 'descricao', label: 'Descrição', span: 2, children: versao.capacidade_snapshot.descricao },
      ]} />
    </Card>}
    <Descriptions size="small" column={1} items={[
      { key: 'entrada', label: 'Recebe', children: <Text className="nexo-preline">{versao.entradas}</Text> },
      { key: 'saida', label: 'Entrega', children: <Text className="nexo-preline">{versao.saidas}</Text> },
      { key: 'limites', label: 'Limites', children: <Text className="nexo-preline">{versao.limites}</Text> },
      { key: 'execucao', label: 'Como usar', children: <Text className="nexo-preline">{versao.execucao}</Text> },
      { key: 'evidencias', label: 'Evidências', children: <Text className="nexo-preline">{versao.evidencias}</Text> },
      { key: 'sha', label: 'Código publicado', children: <Text copyable className="nexo-mono">{versao.commit_sha}</Text> },
      ...(versao.parecer ? [{ key: 'parecer', label: 'Parecer', children: <Text className="nexo-preline">{versao.parecer}</Text> }] : []),
    ]} />
    {(podeEditar || podeHomologar) && <Flex gap={8} wrap="wrap" style={{ marginTop: 12 }}>
      {podeEditar && ['rascunho', 'em_homologacao'].includes(versao.status) && <Button size="small" icon={<EditOutlined />} onClick={editar}>Editar conteúdo</Button>}
      {podeEditar && versao.status === 'rascunho' && <Button size="small" onClick={() => mudarEstado(versao, 'em_homologacao')}>Enviar para homologação</Button>}
      {podeHomologar && versao.status === 'em_homologacao' && <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => mudarEstado(versao, 'homologada')}>Aceitar</Button>}
      {podeHomologar && versao.status === 'em_homologacao' && <Button size="small" icon={<HistoryOutlined />} onClick={() => mudarEstado(versao, 'rascunho')}>Devolver para rascunho</Button>}
      {podeHomologar && versao.status === 'homologada' && <Button size="small" danger icon={<StopOutlined />} onClick={() => mudarEstado(versao, 'suspensa')}>Suspender</Button>}
      {podeHomologar && versao.status === 'suspensa' && <Button size="small" icon={<HistoryOutlined />} onClick={() => mudarEstado(versao, 'homologada')}>Reativar com novo parecer</Button>}
    </Flex>}
  </div>;
}

async function tratarErroMutacao(
  erro: unknown,
  modal: ReturnType<typeof AntApp.useApp>['modal'],
  message: ReturnType<typeof AntApp.useApp>['message'],
  recarregar: () => Promise<void>, fechar: () => void,
) {
  if (conflitoDeRevisao(erro)) {
    modal.warning({ title: 'Os dados mudaram', content: 'Outra pessoa salvou uma alteração. Recarregamos a versão mais recente para você conferir.' });
    fechar(); await recarregar();
  } else message.error(mensagemErro(erro));
}
